<div class="help-container">
  <div class="help-content">
    <h1>Help & Support</h1>
    <p>Answer a couple of quick questions below to get pointed straight at a fix, or browse the FAQ for more general answers.</p>

  </div>

  <!-- Guided Troubleshooting -->
  <div class="troubleshoot-section" id="troubleshoot-section">
    <h2>Guided Troubleshooting</h2>

    <mat-card class="troubleshoot-card">
      <mat-card-content>

        @if (!isTroubleshootAtStart) {
          <div class="troubleshoot-nav">
            <button mat-button (click)="troubleshootBack()">
              <mat-icon>arrow_back</mat-icon>
              Back
            </button>
            <button mat-button (click)="troubleshootRestart()">
              <mat-icon>restart_alt</mat-icon>
              Start Over
            </button>
          </div>
        }

        @if (currentTroubleshootNode.loginRequired) {
          <div class="troubleshoot-result troubleshoot-login-wall">
            <mat-icon class="login-wall-icon">lock</mat-icon>
            <h3>{{ currentTroubleshootNode.question }}</h3>
            <p>{{ currentTroubleshootNode.resultText }}</p>
            <div class="troubleshoot-actions">
              <button mat-raised-button color="primary" routerLink="/login">
                <mat-icon>login</mat-icon>
                Log In
              </button>
            </div>
          </div>
        } @else if (currentTroubleshootNode.options) {
          <div class="troubleshoot-question">
            <h3>{{ currentTroubleshootNode.question }}</h3>
            @if (currentTroubleshootNode.showAccountCheck) {
              <ng-container [ngTemplateOutlet]="accountCheckBanner"></ng-container>
            }
            @if (currentTroubleshootNode.hint) {
              <p class="troubleshoot-hint">{{ currentTroubleshootNode.hint }}</p>
            }
            <div class="troubleshoot-options">
              @for (option of currentTroubleshootNode.options; track option.next) {
                <button mat-stroked-button class="troubleshoot-option" (click)="selectTroubleshootOption(option)">
                  {{ option.label }}
                  <mat-icon>chevron_right</mat-icon>
                </button>
              }
            </div>
          </div>
        } @else {
          <div class="troubleshoot-result">
            <h3>{{ currentTroubleshootNode.question }}</h3>
            @if (currentTroubleshootNode.showAccountCheck) {
              <ng-container [ngTemplateOutlet]="accountCheckBanner"></ng-container>
            }
            <p>{{ currentResultText }}</p>

            @if (currentTroubleshootNode.actions?.length) {
              <div class="troubleshoot-actions">
                @for (action of currentTroubleshootNode.actions; track action.action + action.label) {
                  @if (isTroubleshootActionVisible(action)) {
                    <button mat-raised-button (click)="runTroubleshootAction(action.action)">
                      <mat-icon>{{ action.icon }}</mat-icon>
                      {{ action.label }}
                    </button>
                  }
                }
              </div>
            }
          </div>
        }

      </mat-card-content>
    </mat-card>
  </div>

  <ng-template #accountCheckBanner>
    <div class="troubleshoot-check-banner">
      <mat-icon>fact_check</mat-icon>
      <span>
        Checked your account — VRChat:
        <strong [class.status-good]="isVrchatConnected" [class.status-bad]="!isVrchatConnected">{{ isVrchatConnected ? 'Connected' : 'Not connected' }}</strong>,
        Patreon:
        <strong [class.status-good]="isPatreonActive" [class.status-bad]="!isPatreonActive">{{ patreonStatusLabel }}</strong>
      </span>
    </div>
  </ng-template>

  <!-- FAQ -->
  <div class="faq-section" id="faq-section">
    <h2>Frequently Asked Questions</h2>

    <mat-card class="faq-category">
      <mat-card-header>
        <mat-card-title>
          <mat-icon>account_circle</mat-icon>
          Accounts & Connections
        </mat-card-title>
      </mat-card-header>
      <mat-card-content>

        <mat-expansion-panel class="faq-item">
          <mat-expansion-panel-header>
            <mat-panel-title>How do I connect my VRChat or Patreon account?</mat-panel-title>
          </mat-expansion-panel-header>
          <p>Use the guided troubleshooter above — pick "Connecting or updating my accounts" and it'll take you straight to the right connect button.</p>
          <div class="faq-actions">
            <button mat-raised-button (click)="scrollToTroubleshoot()">
              <mat-icon>troubleshoot</mat-icon>
              Open Troubleshooter
            </button>
          </div>
        </mat-expansion-panel>

        <mat-expansion-panel class="faq-item">
          <mat-expansion-panel-header>
            <mat-panel-title>I changed my VRChat username. What should I do?</mat-panel-title>
          </mat-expansion-panel-header>
          <p>Your connection is tied to your VRChat account, not your display name, but if perks stop working after a rename, reconnect using your new profile link or display name so we can find you. Allow up to 15 minutes to process.</p>
          <div class="faq-actions" *ngIf="accountService.account.value?.connections?.vrchat">
            <button mat-raised-button (click)="openConnectVRChatDialog()">
              <mat-icon>update</mat-icon>
              Update VRChat Connection
            </button>
          </div>
        </mat-expansion-panel>

        <mat-expansion-panel class="faq-item">
          <mat-expansion-panel-header>
            <mat-panel-title>How do I know if my account is properly connected?</mat-panel-title>
          </mat-expansion-panel-header>
          <p>Check your connection status below. Both VRChat and Patreon should show as connected, with your active subscription tier displayed.</p>
          <div class="faq-actions" *ngIf="accountService.account.value">
            <div class="connection-status">
              <strong>VRChat:</strong>
              <span [class.connected]="accountService.account.value.connections.vrchat" [class.disconnected]="!accountService.account.value.connections.vrchat">
                {{ accountService.account.value.connections.vrchat ? 'Connected' : 'Disconnected' }}
              </span>
              <br>
              <strong>Patreon:</strong>
              <span [class.connected]="accountService.account.value.connections.patreon" [class.disconnected]="!accountService.account.value.connections.patreon">
                {{ accountService.account.value.connections.patreon ? 'Connected' : 'Disconnected' }}
              </span>
              @if (accountService.account.value.connections.patreon) {
                <br>
                <strong>Tier:</strong>
                <span [style.color]="accountService.account.value.connections.patreon.subscriptionTier.color">
                  {{ accountService.account.value.connections.patreon.subscriptionTier.name }}
                </span>
              }
            </div>
          </div>
        </mat-expansion-panel>

      </mat-card-content>
    </mat-card>

    <mat-card class="faq-category">
      <mat-card-header>
        <mat-card-title>
          <mat-icon>star</mat-icon>
          Patreon Perks
        </mat-card-title>
      </mat-card-header>
      <mat-card-content>

        <mat-expansion-panel class="faq-item">
          <mat-expansion-panel-header>
            <mat-panel-title>How do I get my Patreon perks in-game?</mat-panel-title>
          </mat-expansion-panel-header>
          <p>Connect both your VRChat and Patreon accounts with an active membership. New connections are processed roughly every 15 minutes — fully restart VRChat afterward to see your perks.</p>
        </mat-expansion-panel>

        <mat-expansion-panel class="faq-item">
          <mat-expansion-panel-header>
            <mat-panel-title>My perks aren't working. What could be wrong?</mat-panel-title>
          </mat-expansion-panel-header>
          <p>Use the guided troubleshooter above — pick "My in-game perks aren't working" and answer a few quick questions to find the issue (connection, subscription status, or timing).</p>
          <div class="faq-actions">
            <button mat-raised-button (click)="scrollToTroubleshoot()">
              <mat-icon>troubleshoot</mat-icon>
              Open Troubleshooter
            </button>
          </div>
        </mat-expansion-panel>

      </mat-card-content>
    </mat-card>

    <mat-card class="faq-category">
      <mat-card-header>
        <mat-card-title>
          <mat-icon>info</mat-icon>
          About Jar.games
        </mat-card-title>
      </mat-card-header>
      <mat-card-content>

        <mat-expansion-panel class="faq-item">
          <mat-expansion-panel-header>
            <mat-panel-title>What is Jar.games?</mat-panel-title>
          </mat-expansion-panel-header>
          <p>Jar.games is the online hub for VRChat content creator Jar. Patreon supporters manage their in-game perks here, and the community can connect with Jar's projects and support resources.</p>
        </mat-expansion-panel>

        <mat-expansion-panel class="faq-item">
          <mat-expansion-panel-header>
            <mat-panel-title>How can I get more help?</mat-panel-title>
          </mat-expansion-panel-header>
          <p>Use the "Get Help" button in the corner of the screen to ask CultBot for instant answers, or join the Discord community for help from staff and other players.</p>
          <div class="faq-actions">
            <button mat-raised-button (click)="openCultBot()">
              <mat-icon>smart_toy</mat-icon>
              Ask CultBot
            </button>
            <button mat-raised-button color="accent" (click)="openDiscord()">
              <mat-icon>discord</mat-icon>
              Join Discord
            </button>
          </div>
        </mat-expansion-panel>

      </mat-card-content>
    </mat-card>
  </div>
</div>

<!-- Get Help FAB -->
<div class="help-fab-container" [class.open]="helpFabOpen">
  @if (helpFabOpen) {
    <div class="help-fab-backdrop" (click)="closeHelpFab()"></div>
  }

  <div class="help-fab-options">
    <button mat-flat-button class="help-fab-option" (click)="openCultBot()">
      <mat-icon>smart_toy</mat-icon> Ask CultBot
    </button>
    <button mat-flat-button class="help-fab-option" (click)="openDiscord()">
      <mat-icon>discord</mat-icon> Join the Discord 
    </button>
  </div>

  <button mat-fab extended class="help-fab-main" (click)="toggleHelpFab()">
    <mat-icon>{{ helpFabOpen ? 'close' : 'help' }}</mat-icon> Get More Help
  </button>
</div>
